<template>

  <div v-if="!showEditUser && !showInviteUser" data-testid="usermanagement-container" class="h-full m-5 mr-10 overflow-visible overflow-x-scroll overflow-y-scroll pb-36" ref="userContainer">
    <div>
      <table data-testid="usermanagement-user-table" class="min-w-full divide-y divide-gray-200">
        <thead class="hidden w-full bg-gray-100 md:table-header-group ">
          <tr>
            <th scope="col" class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
              {{ $t("userManagement.userTable.name") }}
            </th>
            <th scope="col" class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
              {{ $t("userManagement.userTable.email") }}
            </th>
            <th scope="col" class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
              {{ $t("userManagement.userTable.phone") }}
            </th>
            <th scope="col" class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
              {{ $t("userManagement.userTable.tenants") }}
            </th>
          </tr>
        </thead>
        <tbody data-testid="usermanagement-user-table-body" class="h-full bg-white divide-y-2 divide-gray-400 md:divide-y md:divide-gray-200 md:table-row-group">
          <tr v-for="[id, user] in localUsers" :key="id" :data-testid="`usermanagement-user-row-${user.email}`" class="flex flex-wrap cursor-pointer hover:bg-slate-100 md:table-row even:bg-gray-50 odd:bg-white md:even:bg-white" @click="selectUser(user)">
            <td class="flex flex-1 min-w-full gap-5 px-6 py-3 md:whitespace-nowrap md:table-cell">
              <span class="font-thin md:hidden"> {{ $t("userManagement.userTable.name") }}:</span>
              {{ user.name }}
            </td>
            <td class="flex flex-1 min-w-full gap-5 px-6 py-3 md:whitespace-nowrap md:table-cell">
              <span class="font-thin md:hidden"> {{ $t("userManagement.userTable.email") }}:</span>
              {{ user.email }}
            </td>
            <td class="flex flex-1 min-w-full gap-5 px-6 py-3 md:whitespace-nowrap md:table-cell">
              <span class="font-thin md:hidden">{{ $t("userManagement.userTable.phone") }}:</span>
              {{ user.phone }}
            </td>
            <td class="flex flex-1 min-w-full gap-5 px-6 py-3 md:whitespace-nowrap md:table-cell">
              <span class="font-thin md:hidden"> {{ $t("userManagement.userTable.tenants") }}:</span>
              <div v-html="getTenants(user.tenants)"></div>
            </td>
          </tr>
          <template v-if="loading">
            <tr v-for="row in 5" :key="row" data-testid="usermanagement-user-row-loading" class="animate-pulse">
              <td class="px-1 py-2">
                <div class="w-full h-4 bg-gray-300 rounded"></div>
              </td>
              <td class="px-1 py-2">
                <div class="w-full h-4 bg-gray-300 rounded"></div>
              </td>
              <td class="px-1 py-2">
                <div class="w-full h-4 bg-gray-300 rounded"></div>
              </td>
              <td class="px-1 py-2">
                <div class="w-full h-4 bg-gray-300 rounded"></div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <div class="absolute m-auto bottom-4 right-4">
      <div data-testid="usermanagement-invite-button" class="w-10 h-10 m-auto text-white bg-blue-500 rounded-full shadow-lg cursor-pointer hover:bg-blue-700 active:scale-105 active:shadow-lg" @click="showInviteUser = true">
        <div class="flex justify-center h-full m-auto ">
          <PlusIcon class="relative h-5 m-auto"></PlusIcon>
        </div>
      </div>
    </div>
  </div>

  <InviteUser v-if="showInviteUser" @cancel="showInviteUser = false" @refreshData="refreshData"></InviteUser>
  <EditUser v-if="showEditUser" :user="editUser" @cancel="showEditUser = false" @refreshData="refreshData"> </EditUser>
</template>
<script>
import { useStore } from 'vuex';
import { ref, computed } from 'vue';
import { PlusIcon } from "@heroicons/vue/outline";
import { hasRights, USER_PERMISSIONS } from "@/lib/userHasPermission";
import { useInfiniteScroll } from '@vueuse/core';

import InviteUser from "@/components/InviteUser";
import EditUser from "@/components/EditUser";

export default {
  name: "UserManagement",
  components: {
    PlusIcon,
    InviteUser,
    EditUser,
  },

  setup() {

    const isAdmin = computed(() => hasRights(USER_PERMISSIONS.ADMIN));
    const isViewer = computed(() => hasRights(USER_PERMISSIONS.VIEWER));
    const isSuperAdmin = computed(() => { return store.getters["auth/isSuperAdmin"]; });

    const store = useStore();
    const users = computed(() => store.state.users.all);
    const localUsers = computed(() => filterUsers());

    const loading = computed(() => store.state.loading || store.state.users.loading)

    const currentTenant = ref([])

    const showInviteUser = ref(false)
    const showEditUser = ref(false)
    const editUser = ref({})
    const userContainer = ref(null);

    useInfiniteScroll(userContainer,
      async () => {
        await store.dispatch("users/getUsers");
      },
      {
        distance: 10,
        canLoadMore: () => {
          return store.state.users.hasNextPage || store.state.users.all.size == 0
        },
      })


    const filterUsers = () => {
      if (!isSuperAdmin.value && !isAdmin.value && !isViewer.value) {
        window.location.reload();
      }

      if (users.value.size > 0) {

        if (isSuperAdmin.value) {
          return users.value;


        } else if (isAdmin.value) {

          const currentUser = getUserFromMap(store.state.auth.user.username)
          if (currentUser && currentUser.tenants) {
            currentUser.tenants.map((t) => t.tenant.id)
            currentTenant.value = currentUser.tenants.map((t) => t.tenant.id)

            let tmpUsers = new Map()
            users.value.forEach((user) => {
              if (user.tenants.filter((tenant) => currentTenant.value.some((cT) => cT == tenant.tenant.id)).length > 0)
                tmpUsers.set(user.id, { ...user })
            })
            return tmpUsers;
          }

        } else if (isViewer.value) {
          editUser.value = getUserFromMap(store.state.auth.user.username)
          if (editUser.value) {
            showEditUser.value = true;
          }
        }
      }
      return new Set()
    }

    const getUserFromMap = (id) => {
      let key = [...users.value.keys()].find(k => k.includes(id))
      return users.value.get(key);
    }

    const getTenants = (data) => {
      let tenants = "";

      for (let tenant of data.slice(0, 2)) {
        tenants += "<span class='text-sm font-light'>" + tenant.role + "</span>: <span class='font-medium'>" + tenant.tenant.name + "</span> "
      }

      return data.length > 2 ? tenants + "..." : tenants;
    }

    const selectUser = (user) => {
      editUser.value = {};
      showEditUser.value = true;
      editUser.value = user;
    }

    const refreshData = async () => {
      await Promise.all([
        store.dispatch("loadTenants", true),
        store.dispatch("users/getUsers", true)
      ]);

      editUser.value = {};
    }

    return {
      users,
      showInviteUser,
      showEditUser,
      editUser,
      getTenants,
      selectUser,
      refreshData,
      isViewer,
      isAdmin,
      currentTenant,
      loading,
      localUsers,
      filterUsers,
      userContainer
    }
  },

};
</script>
